/**
 * Client-side geofence helper. We still re-validate on the server — never
 * trust the client. This is purely to fail-fast in the UI so the user
 * doesn't get a 422 back.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_M = 6_371_000;
const toRad = (d: number) => (d * Math.PI) / 180;

export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(x)));
}

export function withinGeofence(user: LatLng, stall: LatLng, radius_m = 100): boolean {
  return haversineMeters(user, stall) <= radius_m;
}

export interface GeoLocation extends LatLng {
  accuracy_m: number;
}

export type GeoErrorKind =
  | 'unavailable' // navigator.geolocation not present
  | 'denied' // user denied the permission prompt (PositionError.code 1)
  | 'timeout' // PositionError.code 3
  | 'position_unavailable'; // PositionError.code 2

export class GeoError extends Error {
  kind: GeoErrorKind;
  constructor(kind: GeoErrorKind, message?: string) {
    super(message ?? kind);
    this.name = 'GeoError';
    this.kind = kind;
  }
}

export function getCurrentLocation(timeout_ms = 8000): Promise<GeoLocation> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new GeoError('unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy_m: p.coords.accuracy,
        }),
      (err) => {
        // PositionError.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        const kind: GeoErrorKind =
          err.code === 1 ? 'denied' : err.code === 3 ? 'timeout' : 'position_unavailable';
        reject(new GeoError(kind, err.message));
      },
      { enableHighAccuracy: true, timeout: timeout_ms, maximumAge: 30_000 },
    );
  });
}
